import { Tick } from '@gds/Icons/Paths/Tick';
import { Calculation, CalculationProduct } from '../../../Entities/Calculation.entity';

import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import {
  isLeasingFinancing,
  isStandardFinancing,
  isThreeWayFinancing,
  isTwoWayFinancing,
} from '../../../Utils/isFinanceCaptive';
import { Typography } from '@gds/Typography/Typography';
import { useAtomValue } from 'jotai';
import { CalculatorMessages } from '../Calculator.messages';

import { isPfs } from '../Helpers/FinanceMap';
import styles from './Summary.module.css';

type SummaryDEProps = {
  calculation: Calculation;
  product: CalculationProduct;
  locale: Locale;
  summaryStrings: CalculatorMessages;
};

export const SummaryDE = ({ product, locale, summaryStrings, calculation }: SummaryDEProps) => {
  const vehicle = useAtomValue(currentVehicleAtom);
  const hasStandardFinancing = isStandardFinancing(calculation);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasThreeWayFinancing = isThreeWayFinancing(calculation);
  const hasTwoWayFinancing = isTwoWayFinancing(calculation);
  const showFinalRate = hasTwoWayFinancing || hasThreeWayFinancing;
  const showCreditFinancing = hasThreeWayFinancing || hasStandardFinancing || hasTwoWayFinancing;
  const selectedProduct = calculation?.selectedProduct;
  const captiveId = calculation?.captiveId;
  const isCurrentPorsche = isPfs(captiveId) && selectedProduct === 'privateLeasing';
  const configTwoDecimal = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  const summaryComponent = (
    <summary className={styles.container}>
      <ul className={styles.summary}>
        <li data-id="instalments-container" className={styles.instalmentsContainer}>
          <ul className={styles.instalmentsList}>
            <li data-id="instalments" className={styles.instalments}>
              <span>
                {summaryStrings.monthPrefix} {'  '}
                {hasTwoWayFinancing
                  ? product?.inputs?.months.value - 1
                  : product?.inputs?.months.value}{' '}
                {isCurrentPorsche
                  ? summaryStrings.totalLeasingMonthlyRate
                  : summaryStrings.totalMonthlyRate}
              </span>

              <span className={styles.totalMonthlyRate}>
                {getFormattedCurrency(
                  product.calculationResult.monthlyRate,
                  locale,
                  configTwoDecimal,
                )}
              </span>
            </li>

            {showFinalRate && (
              <li data-id="instalments" className={styles.instalments}>
                <Typography Component="span" variant="body2" className={styles.totalAmountLabel}>
                  {hasTwoWayFinancing ? (
                    <>
                      {summaryStrings.finalPayment} ({product?.inputs?.months.value}.{' '}
                      {summaryStrings.monthSuffix})
                    </>
                  ) : hasThreeWayFinancing ? (
                    <>
                      {summaryStrings.finalPayment} ({product?.inputs?.months.value + 1}.{' '}
                      {summaryStrings.monthSuffix})
                    </>
                  ) : null}
                </Typography>
                <Typography Component="span" variant="body2">
                  {getFormattedCurrency(
                    product.calculationResult.residualPrice,
                    locale,
                    configTwoDecimal,
                  )}
                </Typography>
              </li>
            )}
          </ul>
        </li>

        <li data-id="fees">
          <ul>
            {hasLeasingFinancing && (
              <>
                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.specialPayment}
                  </Typography>
                  <Typography Component="span" variant="body4">
                    {getFormattedCurrency(product?.inputs?.deposit.value, locale, configTwoDecimal)}
                  </Typography>
                </li>
                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.financeMileage}
                  </Typography>
                  <Typography Component="span" variant="body4">
                    {product.inputs.mileage.value} {summaryStrings.financeMileageLabel}
                  </Typography>
                </li>
                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.leasingDuration}
                  </Typography>
                  <Typography Component="span" variant="body4">
                    {product.inputs.months.value} {summaryStrings.monthsInput}
                  </Typography>
                </li>
              </>
            )}

            {showCreditFinancing && (
              <>
                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.universalVehiclePrice}
                  </Typography>
                  <Typography Component="span" variant="body4">
                    {getFormattedCurrency(vehicle?.pricing?.price, locale, configTwoDecimal)}
                  </Typography>
                </li>

                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.oneTimePayment}
                  </Typography>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(product?.inputs?.deposit.value, locale, configTwoDecimal)}
                  </Typography>
                </li>

                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.netLoanUniversalAmount}
                  </Typography>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(
                      product.calculationResult.netLoanAmount,
                      locale,
                      configTwoDecimal,
                    )}
                  </Typography>
                </li>

                <li>
                  <Typography Component="span" variant="body2">
                    {summaryStrings.totalInterestAmount}
                  </Typography>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(
                      product.calculationResult.totalInterestAmount,
                      locale,
                      configTwoDecimal,
                    )}
                  </Typography>
                </li>

                <li>
                  <span>
                    <span className={styles.infoIcon}>
                      <Tick fontSize={20} />
                    </span>
                    {summaryStrings.taeg} {product.calculationResult.nominalInterestRate}%
                  </span>
                  <Typography Component="span" variant="body2">
                    {''}
                  </Typography>
                </li>

                <li>
                  <span>
                    <span className={styles.infoIcon}>
                      <Tick fontSize={20} />
                    </span>
                    {summaryStrings.yearlyInterest} {product.calculationResult.annualInterestRate}%
                  </span>
                  <Typography Component="span" variant="body2">
                    {''}
                  </Typography>
                </li>

                <li>
                  <span>{summaryStrings.totalAmountPayable}</span>
                  <Typography Component="span" variant="body2">
                    {getFormattedCurrency(
                      product.calculationResult.totalAmount,
                      locale,
                      configTwoDecimal,
                    )}
                  </Typography>
                </li>
              </>
            )}
          </ul>
        </li>
      </ul>
    </summary>
  );
  return summaryComponent;
};
